const roundDownTo5 = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  if (numeric <= 5) return numeric; // don't round down to 0 for small fees
  return Math.floor(numeric / 5) * 5;
};

const convertCurrency = (amount, fromCurrency, toCurrency, exchangeRate) => {
  if (fromCurrency === toCurrency) return amount;
  if (fromCurrency === 'EUR' && toCurrency === 'USD') {
    return roundDownTo5(amount * (exchangeRate || 1.1));
  }
  if (fromCurrency === 'USD' && toCurrency === 'EUR') {
    return roundDownTo5(amount / (exchangeRate || 1.1));
  }
  return amount;
};

const discountMultiplier = (value, fallback) => {
  const discount = Number(value);
  const normalizedDiscount = Number.isFinite(discount)
    ? Math.min(Math.max(discount, 0), 100)
    : fallback;
  return 1 - (normalizedDiscount / 100);
};

const resolveStandardFee = ({ fees, now, tier = null }) => {
  const baseFee = typeof fees.base === 'number'
    ? fees.base
    : (typeof fees.standard === 'number' ? fees.standard : 500);
  const earlyBirdFee = typeof fees.earlyBird === 'number' ? fees.earlyBird : baseFee;
  const regularFee = typeof fees.regular === 'number' ? fees.regular : baseFee + 100;
  const lateFee = typeof fees.late === 'number' ? fees.late : regularFee * 1.2;

  if (tier === 'early') return roundDownTo5(earlyBirdFee);
  if (tier === 'regular') return roundDownTo5(regularFee);
  if (tier === 'late') return roundDownTo5(lateFee);

  const earlyDeadline = fees.earlyBirdDeadline ? new Date(fees.earlyBirdDeadline) : null;
  const regularDeadline = fees.regularRegistrationDeadline ? new Date(fees.regularRegistrationDeadline) : null;

  if (earlyDeadline && now <= earlyDeadline) {
    return roundDownTo5(earlyBirdFee);
  }
  if (regularDeadline && now <= regularDeadline) {
    return roundDownTo5(regularFee);
  }
  if (regularDeadline) {
    return roundDownTo5(lateFee);
  }

  return roundDownTo5(baseFee);
};

export const calculateRegistrationFee = ({
  feeType,
  fees,
  accompaniedPersonsCount = 0,
  now = new Date(),
  tier = null,
  paymentCurrency = null
}) => {
  const normalizedFees = fees || {};
  const count = Math.min(Math.max(Number(accompaniedPersonsCount) || 0, 0), 1);
  const useTiered = typeof normalizedFees.base === 'number' ||
    typeof normalizedFees.earlyBird === 'number' ||
    typeof normalizedFees.regular === 'number' ||
    typeof normalizedFees.late === 'number' ||
    normalizedFees.earlyBirdDeadline ||
    normalizedFees.regularRegistrationDeadline;

  let standardFee = 0;
  let studentFee = 0;
  let accompaniedFee = 0;

  if (useTiered) {
    standardFee = resolveStandardFee({ fees: normalizedFees, now, tier });
    studentFee = roundDownTo5(standardFee * discountMultiplier(normalizedFees.doctoralStudentDiscount, 25));
    accompaniedFee = roundDownTo5(standardFee * discountMultiplier(normalizedFees.accompaniedPersonDiscount, 50));
  } else {
    standardFee = typeof normalizedFees.standard === 'number' ? normalizedFees.standard : 500;
    studentFee = typeof normalizedFees.student === 'number' ? normalizedFees.student : 250;
    accompaniedFee = typeof normalizedFees.accompaniedPerson === 'number' ? normalizedFees.accompaniedPerson : 100;
  }

  const base = feeType === 'student' ? studentFee : standardFee;
  const totalInBaseCurrency = base + (count * accompaniedFee);
  
  // Convert to payment currency if different from base currency
  const baseCurrency = normalizedFees.currency || 'EUR';
  const targetCurrency = paymentCurrency || baseCurrency;
  const exchangeRate = normalizedFees.exchangeRate || 1.1;
  
  if (targetCurrency !== baseCurrency) {
    return convertCurrency(totalInBaseCurrency, baseCurrency, targetCurrency, exchangeRate);
  }
  
  return totalInBaseCurrency;
};

export const calculateFeeBreakdown = ({ feeType, fees, accompaniedPersonsCount = 0, now = new Date(), tier = null, paymentCurrency = null }) => {
  const normalizedFees = fees || {};
  const count = Math.min(Math.max(Number(accompaniedPersonsCount) || 0, 0), 1);
  const useTiered = typeof normalizedFees.base === 'number' ||
    typeof normalizedFees.earlyBird === 'number' ||
    typeof normalizedFees.regular === 'number' ||
    typeof normalizedFees.late === 'number' ||
    normalizedFees.earlyBirdDeadline ||
    normalizedFees.regularRegistrationDeadline;

  let standardFee = 0;
  let studentFee = 0;
  let accompaniedFee = 0;

  if (useTiered) {
    standardFee = resolveStandardFee({ fees: normalizedFees, now, tier });
    studentFee = roundDownTo5(standardFee * discountMultiplier(normalizedFees.doctoralStudentDiscount, 25));
    accompaniedFee = roundDownTo5(standardFee * discountMultiplier(normalizedFees.accompaniedPersonDiscount, 50));
  } else {
    standardFee = typeof normalizedFees.standard === 'number' ? normalizedFees.standard : 500;
    studentFee = typeof normalizedFees.student === 'number' ? normalizedFees.student : 250;
    accompaniedFee = typeof normalizedFees.accompaniedPerson === 'number' ? normalizedFees.accompaniedPerson : 100;
  }

  const base = feeType === 'student' ? studentFee : standardFee;
  const totalInBaseCurrency = base + (count * accompaniedFee);
  
  // Convert to payment currency if different from base currency
  const baseCurrency = normalizedFees.currency || 'EUR';
  const targetCurrency = paymentCurrency || baseCurrency;
  const exchangeRate = normalizedFees.exchangeRate || 1.1;
  
  let displayBase = base;
  let displayAccompaniedFee = accompaniedFee;
  let displayTotal = totalInBaseCurrency;
  
  if (targetCurrency !== baseCurrency) {
    displayBase = convertCurrency(base, baseCurrency, targetCurrency, exchangeRate);
    displayAccompaniedFee = convertCurrency(accompaniedFee, baseCurrency, targetCurrency, exchangeRate);
    displayTotal = convertCurrency(totalInBaseCurrency, baseCurrency, targetCurrency, exchangeRate);
  }
  
  return {
    base: displayBase,
    accompaniedFee: displayAccompaniedFee,
    total: displayTotal,
    currency: targetCurrency
  };
};
